import { Controller, useFormContext, useWatch } from "react-hook-form";

import { cn, DateField } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import { formatSeasonDate, parseSeasonDate, type SummerWinterSeasonType } from "../helpers";
import { SummerWinterScheme } from "./summer-winter-scheme";

interface SummerWinterProps {
  disabled?: boolean;
}

export const SummerWinter: React.FC<SummerWinterProps> = ({ disabled = false }) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<SummerWinterSeasonType>();

  const summerStart = useWatch({ control, name: "summerPeriods.begin" });
  const summerEnd = useWatch({ control, name: "summerPeriods.end" });
  const winterStart = useWatch({ control, name: "winterPeriods.begin" });
  const winterEnd = useWatch({ control, name: "winterPeriods.end" });

  return (
    <div>
      <div className={cn("gap-y-2.5 flex flex-col")}>
        <label
          className={cn(
            "mb-0 leading-normal font-medium max-w-62.5 grid grid-cols-[55px_auto_auto_auto] items-center text-base",
          )}
        >
          {m.winter()}
          <Controller
            control={control}
            name="winterPeriods.begin"
            render={({ field: { value, onChange } }) => (
              <DateField.Root
                value={value ? parseSeasonDate(value) : null}
                onValueChange={date => {
                  if (!date) return;
                  onChange(formatSeasonDate(date));
                  setValue("summerPeriods.end", formatSeasonDate(date.subtract(1, "day")));
                }}
                format="DD/MM"
                disabled={disabled}
              >
                <DateField.Input />
              </DateField.Root>
            )}
          />
          <span>-</span>
          <Controller
            control={control}
            name="winterPeriods.end"
            render={({ field: { value, onChange } }) => (
              <DateField.Root
                value={value ? parseSeasonDate(value) : null}
                onValueChange={date => {
                  if (!date) return;
                  onChange(formatSeasonDate(date));
                  setValue("summerPeriods.begin", formatSeasonDate(date.add(1, "day")));
                }}
                format="DD/MM"
                disabled={disabled}
              >
                <DateField.Input />
              </DateField.Root>
            )}
          />
        </label>
        <label
          className={cn(
            "mb-0 leading-normal font-medium max-w-62.5 grid grid-cols-[55px_auto_auto_auto] items-center text-base",
          )}
        >
          {m.summer()}
          <Controller
            name="summerPeriods.begin"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DateField.Root
                value={value ? parseSeasonDate(value) : null}
                onValueChange={date => {
                  if (!date) return;
                  onChange(formatSeasonDate(date));
                  setValue("winterPeriods.end", formatSeasonDate(date.subtract(1, "day")));
                }}
                format="DD/MM"
                disabled={disabled}
              >
                <DateField.Input />
              </DateField.Root>
            )}
          />
          <span>-</span>
          <Controller
            name="summerPeriods.end"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DateField.Root
                value={value ? parseSeasonDate(value) : null}
                onValueChange={date => {
                  if (!date) return;
                  onChange(formatSeasonDate(date));
                  setValue("winterPeriods.begin", formatSeasonDate(date.add(1, "day")));
                }}
                format="DD/MM"
                disabled={disabled}
              >
                <DateField.Input />
              </DateField.Root>
            )}
          />
        </label>
        {(!!errors.summerPeriods || !!errors.winterPeriods) && (
          <p className={cn("text-base text-primary-red")}>{m.fields_not_required()}</p>
        )}
      </div>
      <SummerWinterScheme
        className={cn("mt-5")}
        summerStart={summerStart}
        summerEnd={summerEnd}
        winterStart={winterStart}
        winterEnd={winterEnd}
      />
    </div>
  );
};
